import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { TextField, InputAdornment, IconButton, Popper, Paper, ClickAwayListener } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs, { Dayjs } from 'dayjs';
import { parseDateShortcut, formatDateOnly } from '@/lib/dateShortcuts';

export interface DateShortcutFieldProps {
  /** The date as YYYY-MM-DD; empty for none. */
  value: string | undefined | null;
  /** Called with the date as YYYY-MM-DD, or undefined when the field is cleared. */
  onChange: (value: string | undefined) => void;
  label: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** An error from outside the field (validation); a typed value that does not parse shows its own. */
  error?: string;
}

/**
 * A date field with F&O-style keyboard shortcuts, typed and committed on Enter, Tab or blur, and a calendar beside it
 * — the field of the JSONForms date renderer, for a form outside JSONForms.
 *
 * Supports:
 * - `t` or `T` → today's date
 * - `+N` / `-N` → relative day offsets
 * - `D` or `DD` → day in current month
 * - `MMDD` → month + day (nearest date rule)
 * - `YYMMDD` → year + month + day
 * - Standard formats: YYYY-MM-DD, YYYYMMDD, DD/MM, DD/MM/YYYY
 */
export function DateShortcutField({ value, onChange, label, required, disabled, error }: DateShortcutFieldProps) {
  // Local input state (what user types)
  const [inputValue, setInputValue] = useState<string>('');
  // Whether the calendar picker is open
  const [pickerOpen, setPickerOpen] = useState(false);
  // Local error state for parsing errors
  const [parseError, setParseError] = useState<string | null>(null);
  // Ref for anchoring the popper
  const anchorRef = useRef<HTMLDivElement>(null);

  // Sync input value with the value given
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  // Parse and commit value on blur/enter/tab
  const commitValue = useCallback(() => {
    const trimmed = inputValue.trim();

    // Empty input
    if (!trimmed) {
      setParseError(null);
      onChange(undefined);
      return;
    }

    // Try to parse using shortcuts
    const result = parseDateShortcut(trimmed);

    if (result.success && result.value) {
      const formatted = formatDateOnly(result.value.date);
      setParseError(null);
      setInputValue(formatted);
      onChange(formatted);
    } else {
      // Keep the invalid input, show error
      setParseError(result.error || 'Invalid date');
    }
  }, [inputValue, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Clear parse error while typing
    setParseError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      commitValue();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't commit if clicking on the calendar
    if (anchorRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    commitValue();
  };

  const handlePickerChange = (picked: Dayjs | null) => {
    if (picked && picked.isValid()) {
      const formatted = picked.format('YYYY-MM-DD');
      setInputValue(formatted);
      setParseError(null);
      onChange(formatted);
      setPickerOpen(false);
    }
  };

  const errorMessage = parseError || error;

  // Parse current value for picker (null when empty so today isn't pre-selected)
  const parsedValue = value ? dayjs(value) : null;
  const pickerValue = parsedValue?.isValid() ? parsedValue : null;

  return (
    <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
      <div ref={anchorRef}>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          label={label}
          required={required}
          disabled={disabled}
          error={!!errorMessage}
          helperText={errorMessage}
          fullWidth
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setPickerOpen((prev) => !prev)}
                  size="small"
                  edge="end"
                  disabled={disabled}
                  sx={{ py: 0 }}
                >
                  <CalendarTodayIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Popper
          open={pickerOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
        >
          <Paper elevation={8}>
            <StaticDatePicker
              value={pickerValue}
              onChange={handlePickerChange}
              slotProps={{ actionBar: { actions: [] } }}
            />
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
